import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

export type AssistantProductContext = {
  id?: string;
  name: string;
  brand?: string;
  product_type?: string;
  category?: string;
  price?: number;
  currency?: string;
  description?: string;
  specs?: Record<string, unknown>;
  url?: string;
};

type Ctx = {
  product: AssistantProductContext | null;
  setProduct: (p: AssistantProductContext | null) => void;
  open: boolean;
  setOpen: (v: boolean) => void;
  openWithQuery: (q: string) => void;
  pendingQuery: string | null;
  consumePendingQuery: () => string | null;
};

const AssistantCtx = createContext<Ctx | null>(null);

export function AssistantProvider({ children }: { children: ReactNode }) {
  const [product, setProduct] = useState<AssistantProductContext | null>(null);
  const [open, setOpen] = useState(false);
  const [pendingQuery, setPendingQuery] = useState<string | null>(null);

  const openWithQuery = useCallback((q: string) => {
    setPendingQuery(q);
    setOpen(true);
  }, []);

  const consumePendingQuery = useCallback(() => {
    const q = pendingQuery;
    setPendingQuery(null);
    return q;
  }, [pendingQuery]);

  const value = useMemo(
    () => ({ product, setProduct, open, setOpen, openWithQuery, pendingQuery, consumePendingQuery }),
    [product, open, openWithQuery, pendingQuery, consumePendingQuery]
  );

  return <AssistantCtx.Provider value={value}>{children}</AssistantCtx.Provider>;
}

export function useAssistant() {
  const ctx = useContext(AssistantCtx);
  if (!ctx) throw new Error("useAssistant must be used within AssistantProvider");
  return ctx;
}
